import { CSSProperties, FC, useMemo, useRef, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { useLazyLoadQuery } from 'react-relay';
import { getAllUnitsGraphQL } from '../../graphQLOperations/Dashboard';
import { DashboardAllUnitsQuery } from '../../graphQLOperations/__generated__/DashboardAllUnitsQuery.graphql';
import { useUnitFocus } from '../../custom-hooks/useUnitFocus';
import { UnitFocusActions } from './UnitFocusActions';
import { ActiveFocusBanner } from './ActiveFocusBanner';
import { ActivateFocusPopover, ActivateFocusPopoverHandle } from '../UnitFocus/ActivateFocusPopover';
import { EditFocusSettingsPopover, EditFocusSettingsPopoverHandle } from '../UnitFocus/EditFocusSettingsPopover';
import { MoveFocusPopover, MoveFocusPopoverHandle } from '../UnitFocus/MoveFocusPopover';
import { useRoadmapMembership } from '../../custom-hooks/useFocusClassData';
import { useRoadmapUnitCompletion } from '../../custom-hooks/useFocusEligibility';
import { useAppSelector } from '../../redux/hooks';
import { selectedTeacherDashboardFilter } from '../../redux/reducers/teacherDashboardFiltersSlice';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useClassFocusOnboarding } from '../../contexts/ClassFocusOnboardingContext';

interface Props {
  selectedRoadmapId: string;
  roadmapName: string;
  persistedActiveTargetIds: string[];
  selectedUnitId: string;
  onSelectUnit: (unitId: string) => void;
}

interface UnitOption {
  label: string;
  unitName: string;
  summary: string;
  value: string;
}

// activeFocusClassIds is empty until the teacher-wide focus-classes query resolves after a reload,
// so fall back to the single viewed class.
const FALLBACK_SHARED_CLASS_COUNT = 1;

const DROPDOWN_STYLE: CSSProperties = {
  flex: '1 1 0',
  minWidth: 0,
  height: 44,
  alignItems: 'center',
  border: '2px solid #F1F0EE',
  borderRadius: 12,
};

export const UnitFocusWidget: FC<Props> = ({
  selectedRoadmapId,
  roadmapName,
  persistedActiveTargetIds = [],
  selectedUnitId,
  onSelectUnit,
}) => {
  const { pulsing, markClassFocusCompleted, stopPulsing } = useClassFocusOnboarding();
  const { activeTargetIds, activeFocusClassIds } = useUnitFocus();
  const popoverRef = useRef<ActivateFocusPopoverHandle>(null);
  const editPopoverRef = useRef<EditFocusSettingsPopoverHandle>(null);
  const moveRef = useRef<MoveFocusPopoverHandle>(null);
  const [editOpen, setEditOpen] = useState(false);

  const currentClass = useAppSelector(selectedTeacherDashboardFilter).classes?.[0];
  const currentClassName = currentClass?.name?.replace(/\s+/g, ' ').trim() ?? '';

  const unitsData = useLazyLoadQuery<DashboardAllUnitsQuery>(
    getAllUnitsGraphQL,
    { isActive: 'ACTIVE' },
    { fetchPolicy: 'store-or-network' },
  );

  const unitOptions = useMemo(
    () =>
      (unitsData.allTargets ?? [])
        // Relay allows null list entries; the type guard lets the map below read unit fields safely.
        .filter((t): t is NonNullable<typeof t> => !!t && t.targetGroup?.id === selectedRoadmapId)
        .map(t => ({
          label: `Unit ${t.name}: ${t.summary}`,
          unitName: `Unit ${t.name}`,
          summary: t.summary ?? '',
          value: t?.id ?? '',
        }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true })),
    [unitsData, selectedRoadmapId],
  );

  const renderUnitLabel = (option?: UnitOption) => {
    if (!option) return null;
    return (
      <span>
        <span className="tw-font-semibold">{option.unitName}:</span> {option.summary}
      </span>
    );
  };

  const effectiveUnitId = selectedUnitId || unitOptions[0]?.value || '';

  const focusClassIds = activeFocusClassIds[effectiveUnitId];
  const classInFocusScope = !focusClassIds || !currentClass?.id || focusClassIds.includes(currentClass.id);
  const hasActiveFocus =
    effectiveUnitId !== '' &&
    classInFocusScope &&
    (persistedActiveTargetIds.includes(effectiveUnitId) || activeTargetIds.includes(effectiveUnitId));

  const sharedClassCount = focusClassIds?.length ?? FALLBACK_SHARED_CLASS_COUNT;

  const orderedUnitIds = useMemo(() => unitOptions.map(o => o.value), [unitOptions]);

  // Classes that share the active focus (falls back to the viewed class after a reload).
  const scopeClassIds = useMemo(() => {
    return focusClassIds?.length ? focusClassIds : currentClass?.id ? [currentClass.id] : [];
  }, [focusClassIds, currentClass]);

  // Completion powers the Move flow: the next-unit default and whether there's anywhere to move.
  // Gated on hasActiveFocus so only widgets that actually show the Move control pay for the queries.
  const completionMembership = useRoadmapMembership(hasActiveFocus, scopeClassIds, selectedRoadmapId);
  const completionMemberFilter: ReadonlySet<string> | null =
    completionMembership.loading || completionMembership.error ? null : completionMembership.memberIds;
  const completion = useRoadmapUnitCompletion(
    hasActiveFocus,
    selectedRoadmapId,
    orderedUnitIds,
    scopeClassIds,
    completionMemberFilter,
  );

  return (
    <div className="tw-flex tw-flex-col tw-gap-4 tw-w-full">
      <div className="tw-flex tw-gap-4 tw-w-full tw-items-start">
        <Dropdown
          value={effectiveUnitId}
          options={unitOptions}
          onChange={e => onSelectUnit(e.value)}
          optionLabel="label"
          optionValue="value"
          itemTemplate={renderUnitLabel}
          valueTemplate={(option, props) => renderUnitLabel(option) ?? <span>{props.placeholder}</span>}
          placeholder={unitOptions.length ? `Select a unit for ${roadmapName}` : `No units found for ${roadmapName}`}
          disabled={!unitOptions.length || hasActiveFocus}
          style={DROPDOWN_STYLE}
          className={pulsing && !hasActiveFocus ? 'nc_pulse-attention' : undefined}
        />
        <UnitFocusActions
          hasActiveFocus={hasActiveFocus}
          disabled={!effectiveUnitId}
          editActive={editOpen}
          // Active focus → the primary button moves the focus (and hosts the End-focus link,
          // which must stay reachable even when there's nowhere to move); unfocused → it sets one.
          onPrimaryClick={e => (hasActiveFocus ? moveRef.current?.toggle(e) : popoverRef.current?.toggle(e))}
          onEditClick={e => editPopoverRef.current?.toggle(e)}
        />
        <ActivateFocusPopover
          ref={popoverRef}
          targetId={effectiveUnitId}
          roadmapId={selectedRoadmapId}
          classId={currentClass?.id ?? ''}
          className={currentClassName}
          renderTrigger={false}
          onFocusActivated={markClassFocusCompleted}
        />
        <EditFocusSettingsPopover
          ref={editPopoverRef}
          targetId={effectiveUnitId}
          roadmapId={selectedRoadmapId}
          classIds={scopeClassIds}
          onVisibleChange={setEditOpen}
        />
        <MoveFocusPopover
          ref={moveRef}
          fromTargetId={effectiveUnitId}
          roadmapId={selectedRoadmapId}
          roadmapName={roadmapName}
          classId={currentClass?.id ?? ''}
          className={currentClassName}
          units={unitOptions}
          completedUnitIds={completion.completedUnitIds}
          onMoved={onSelectUnit}
          onEnded={() => onSelectUnit(unitOptions[0]?.value ?? '')}
          onEndFocusFlow={stopPulsing}
        />
      </div>
      {hasActiveFocus && <ActiveFocusBanner classCount={sharedClassCount} />}
      <div className="tw-self-stretch tw-border-t tw-inline-flex tw-flex-col tw-justify-end tw-items-end tw-gap-1">
        <div className="tw-self-stretch tw-h-0.5 tw-relative tw-bg-neutral-100"></div>
        <div className="tw-pt-2 tw-pb-1 tw-border-b tw-border-indigo-600 tw-justify-end tw-items-center tw-overflow-hidden">
          <Link
            to="/units"
            state={{
              roadmap: selectedRoadmapId,
              roadmapLabel: roadmapName,
              unit: effectiveUnitId,
            }}
            className="tw-text-center tw-justify-center tw-gap-2 tw-inline-flex tw-text-indigo-600 tw-text-base tw-font-bold tw-font-atkinson tw-leading-4"
          >
            View unit details
            <ArrowRight className="tw-size-4 tw-text-indigo-600" />
          </Link>
        </div>
      </div>
    </div>
  );
};
